import { Dayjs } from 'dayjs';
import { useEffect, useRef } from 'react';

import { BookingModeDto } from '@/api/generated';
import {
  ProcessCalendarEntryQueryFormRange,
  ProcessCalendarEntryQueryIsoRange,
} from '@/views/processes/calendarEntryQuery/model/ProcessCalendarEntryQueryForm.model.ts';
import { processCalendarEntryQueryUtil } from '@/views/processes/calendarEntryQuery/utils/processCalendarEntryQuery.util.ts';

type Params = {
  bookingMode: BookingModeDto;
  formMethods: RangeFormMethods;
  /** The booking mode the form range is shown in when the form opens. */
  initialBookingMode: BookingModeDto;
  isError: boolean;
  isLoading: boolean;
  /** Prefilled ISO range: read again in the new booking mode while the range is not edited. */
  prefill?: ProcessCalendarEntryQueryIsoRange;
};

type RangeField = keyof ProcessCalendarEntryQueryFormRange;

/** The form methods used on the range fields: any form containing the query range fits. */
type RangeFormMethods = {
  getFieldState: (name: RangeField) => { isDirty: boolean };
  getValues: (name: RangeField) => Dayjs | null;
  setValue: (name: RangeField, value: Dayjs | null) => void;
  watch: (names: readonly ['requestedFrom', 'requestedTo']) => [Dayjs | null, Dayjs | null];
};

/**
 * Shows the form range in the booking mode of the selected type once the mode is known: an edited
 * (or not prefilled) range keeps its meaning, an untouched prefill is read again.
 * Returns whether the unknown booking mode blocks saving the range.
 */
export const useProcessCalendarEntryQueryBookingMode = ({
  bookingMode,
  formMethods,
  initialBookingMode,
  isError,
  isLoading,
  prefill,
}: Params): boolean => {
  const shownBookingModeRef = useRef(initialBookingMode);
  useEffect(() => {
    if (isLoading || isError) return;
    const { getFieldState, getValues, setValue } = formMethods;
    const isRangeEdited =
      getFieldState('requestedFrom').isDirty || getFieldState('requestedTo').isDirty;
    const prefillToRead = isRangeEdited ? undefined : prefill;
    if (!prefillToRead && shownBookingModeRef.current === bookingMode) return;
    const { requestedFrom, requestedTo } = prefillToRead
      ? processCalendarEntryQueryUtil.readRange(prefillToRead, bookingMode)
      : processCalendarEntryQueryUtil.changeBookingMode(
          { requestedFrom: getValues('requestedFrom'), requestedTo: getValues('requestedTo') },
          bookingMode,
        );
    shownBookingModeRef.current = bookingMode;
    setValue('requestedFrom', requestedFrom);
    setValue('requestedTo', requestedTo);
  }, [bookingMode, formMethods, isError, isLoading, prefill]);

  const [requestedFrom, requestedTo] = formMethods.watch(['requestedFrom', 'requestedTo']);
  return processCalendarEntryQueryUtil.isBookingModeError(isError, requestedFrom, requestedTo);
};
